import { apiRequest } from "../../api/apiClient";
import type {
  ApiUser,
  CreateUserDto,
  UpdateUserDto,
} from "../../types/user.types";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ||
        error.title ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json();
};

const appendProfileImageFields = (
  formData: FormData,
  profileImageFile: File,
) => {
  formData.append("ProfileImage", profileImageFile, profileImageFile.name);
  formData.append("profileImage", profileImageFile, profileImageFile.name);
};

export const usersService = {
  getUsers: async (): Promise<ApiUser[]> => {
    const response = await apiRequest("/Users", {
      method: "GET",
    });
    return handleResponse<ApiUser[]>(response);
  },

  getUserById: async (userId: number): Promise<ApiUser> => {
    const response = await apiRequest(`/Users/${userId}`, {
      method: "GET",
    });
    return handleResponse<ApiUser>(response);
  },

  createUser: async (
    data: CreateUserDto,
    profileImageFile?: File,
  ): Promise<ApiUser> => {
    const formData = new FormData();
    // Append each property as a separate form field (case-sensitive)
    formData.append("Username", data.username);
    formData.append("FirstName", data.firstName);
    if (data.middleName) formData.append("MiddleName", data.middleName);
    formData.append("LastName", data.lastName);
    formData.append("GenderId", String(data.genderId));
    if (data.dateOfBirth) formData.append("DateOfBirth", data.dateOfBirth);
    if (data.nationalIdNumber)
      formData.append("NationalIdNumber", data.nationalIdNumber);
    formData.append("Email", data.email);
    if (data.primaryPhone) formData.append("PrimaryPhone", data.primaryPhone);
    if (data.secondaryPhone)
      formData.append("SecondaryPhone", data.secondaryPhone);
    formData.append("RoleId", String(data.roleId));
    formData.append("IsActive", String(data.isActive));
    formData.append("IsConfirmed", String(data.isConfirmed));
    formData.append("IsSystemAdmin", String(data.isSystemAdmin));
    formData.append("IsClientAdmin", String(data.isClientAdmin));
    formData.append("IsUserOnly", String(data.isUserOnly));
    if (!profileImageFile && data.profileImagePath !== undefined) {
      formData.append("ProfileImagePath", data.profileImagePath || "");
      formData.append("profileImagePath", data.profileImagePath || "");
    }
    if (profileImageFile) {
      appendProfileImageFields(formData, profileImageFile);
    }
    const response = await apiRequest("/Users", {
      method: "POST",
      body: formData,
    });
    return handleResponse<ApiUser>(response);
  },

  updateUser: async (
    userId: number,
    data: UpdateUserDto,
    profileImageFile?: File,
  ): Promise<ApiUser> => {
    const formData = new FormData();
    // Append each property as a separate form field (case-sensitive, match backend)
    if (data.username) formData.append("Username", data.username);
    if (data.firstName) formData.append("FirstName", data.firstName);
    if (data.middleName) formData.append("MiddleName", data.middleName);
    if (data.lastName) formData.append("LastName", data.lastName);
    if (data.genderId !== undefined)
      formData.append("GenderId", String(data.genderId));
    if (data.dateOfBirth) formData.append("DateOfBirth", data.dateOfBirth);
    if (data.nationalIdNumber)
      formData.append("NationalIdNumber", data.nationalIdNumber);
    if (data.email) formData.append("Email", data.email);
    if (data.primaryPhone) formData.append("PrimaryPhone", data.primaryPhone);
    if (data.secondaryPhone)
      formData.append("SecondaryPhone", data.secondaryPhone);
    if (data.roleId !== undefined)
      formData.append("RoleId", String(data.roleId));
    if (data.isActive !== undefined)
      formData.append("IsActive", String(data.isActive));
    if (data.isConfirmed !== undefined)
      formData.append("IsConfirmed", String(data.isConfirmed));
    if (data.isSystemAdmin !== undefined)
      formData.append("IsSystemAdmin", String(data.isSystemAdmin));
    if (data.isClientAdmin !== undefined)
      formData.append("IsClientAdmin", String(data.isClientAdmin));
    if (data.isUserOnly !== undefined)
      formData.append("IsUserOnly", String(data.isUserOnly));
    if (data.workflowId !== undefined)
      formData.append("WorkflowId", String(data.workflowId));
    if (data.userNo) formData.append("UserNo", data.userNo);
    if (data.id !== undefined) formData.append("Id", String(data.id));
    if (!profileImageFile && data.profileImagePath !== undefined) {
      formData.append("ProfileImagePath", data.profileImagePath || "");
      formData.append("profileImagePath", data.profileImagePath || "");
    }
    if (profileImageFile) {
      appendProfileImageFields(formData, profileImageFile);
    }
    const response = await apiRequest(`/Users/${userId}`, {
      method: "PUT",
      body: formData,
    });
    return handleResponse<ApiUser>(response);
  },

  deleteUser: async (userId: number): Promise<void> => {
    const response = await apiRequest(`/Users/${userId}`, {
      method: "DELETE",
    });
    await handleResponse<void>(response);
  },

  toggleUserStatus: async (
    userId: number,
    isActive: boolean,
  ): Promise<ApiUser> => {
    const response = await apiRequest(`/Users/${userId}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ isActive }),
    });
    return handleResponse<ApiUser>(response);
  },

  changeStatus: async (userId: number, statusId: number): Promise<ApiUser> => {
    const response = await apiRequest(`/Users/change-status`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId, statusId }),
    });
    return handleResponse<ApiUser>(response);
  },

  uploadSignature: async (
    userId: number,
    signatureFile: File,
  ): Promise<ApiUser> => {
    const formData = new FormData();
    formData.append("file", signatureFile);

    const response = await apiRequest(`/Users/add-signature/${userId}`, {
      method: "PUT",
      body: formData,
    });
    return handleResponse<ApiUser>(response);
  },
};
